// ---------------------------------------------------------------------------
// routeDivergence — compare a route to the DIRECT route and find where they
// actually differ (Morgan: "the difference is the top part of the trip, when it
// veers from the direct route — but that's not mentioned. compare the two routes
// and put into words why we chose the different roads when we did").
//
// We measure, per point along the route, how far it sits from the direct
// route's polyline. Stretches that run far from the direct line are the
// "divergence" — the only part worth explaining, because the shared part is, by
// definition, the same road the direct route uses. We return those stretches as
// distance-fractions along the route, plus the road names the route is on while
// it diverges, and a plain-language position ("toward the end").
// ---------------------------------------------------------------------------

import { cumulativeDistances, haversine, nearestPointOnPolyline, samplePolylineMax, type LngLat } from "@/utils/polyline";

export interface DivergenceSpan {
  /** Start/end as a fraction (0–1) of the route's total distance. */
  startFrac: number;
  endFrac: number;
  /** Midpoint fraction — used for the position phrase. */
  midFrac: number;
  /** How much of the whole trip this divergent stretch covers (0–1). */
  coverFrac: number;
}

export interface RouteDivergence {
  spans: DivergenceSpan[];
  /** Total fraction of the route that runs off the direct line. */
  totalDivergentFrac: number;
}

/** Cumulative distance fraction (0–1) at each coordinate index. */
function cumulativeFractions(coords: LngLat[]): number[] {
  const cum = [0];
  for (let i = 1; i < coords.length; i++) cum.push(cum[i - 1] + haversine(coords[i - 1], coords[i]));
  const total = cum[cum.length - 1] || 1;
  return cum.map((d) => d / total);
}

/**
 * Find where `routeCoords` diverges from `directCoords`. A point is "divergent"
 * when it sits more than `thresholdM` from the direct polyline. Consecutive
 * divergent points become a span. Tiny spans (< minCoverFrac of the trip) are
 * dropped as noise.
 */
export function routeDivergence(
  routeCoords: LngLat[],
  directCoords: LngLat[],
  opts: { thresholdM?: number; minCoverFrac?: number } = {},
): RouteDivergence {
  const thresholdM = opts.thresholdM ?? 140;
  const minCoverFrac = opts.minCoverFrac ?? 0.04;
  if (!routeCoords || routeCoords.length < 2 || !directCoords || directCoords.length < 2) {
    return { spans: [], totalDivergentFrac: 0 };
  }
  // Downsample the direct line for the proximity test (cheap, plenty accurate).
  const directRef = samplePolylineMax(directCoords, 400);
  const fracs = cumulativeFractions(routeCoords);
  // Perpendicular (point-to-segment) distance to the direct line — NOT nearest-
  // vertex, which would falsely flag collinear points between sparse vertices.
  // Built once (PLE-6) instead of once per route point. `distance` never reads it.
  const directCum = cumulativeDistances(directRef);
  const divergent: boolean[] = routeCoords.map(
    (p) => nearestPointOnPolyline(p, directRef, directCum).distance > thresholdM,
  );

  const spans: DivergenceSpan[] = [];
  let i = 0;
  while (i < divergent.length) {
    if (!divergent[i]) { i++; continue; }
    let j = i;
    while (j + 1 < divergent.length && divergent[j + 1]) j++;
    const startFrac = fracs[i];
    const endFrac = fracs[j];
    const coverFrac = endFrac - startFrac;
    if (coverFrac >= minCoverFrac) {
      spans.push({ startFrac, endFrac, midFrac: (startFrac + endFrac) / 2, coverFrac });
    }
    i = j + 1;
  }
  const totalDivergentFrac = spans.reduce((s, sp) => s + sp.coverFrac, 0);
  return { spans, totalDivergentFrac };
}

/** Plain-language position of a divergence along the trip.
 *
 *  Returns "" when we can't say anything useful — callers must omit the phrase
 *  entirely rather than print an empty one.
 *
 *  NO MORE "midway through" (Morgan, Aug 9: "it's often wrong"). The middle
 *  band spans 0.28–0.62 of the trip: on the 144-mile Scenic card that shipped
 *  this line, "midway through" was pointing at a stretch nearly fifty miles
 *  wide. The two ends are defensible — a driver can tell whether a detour
 *  happened near the start or near the finish — but the middle is a guess
 *  dressed as a fact, and the roads named beside it are true on their own.
 *  Better to say "Takes Lafayette Road and Newbury Street" and stop. */
export function describeDivergencePosition(midFrac: number): string {
  // RETIRED (Morgan, Aug 9: "just say it takes those roads, no time, distance
  // etc"). Every band is now empty. The card names the roads and stops.
  //
  // Kept as a function rather than ripped out because the call sites already
  // handle "" correctly and a one-line return is a smaller surface than
  // rewiring them — and because if a position claim ever earns its way back it
  // belongs here, measured, rather than reconstructed from scratch.
  void midFrac;
  return "";
}

/**
 * The road names the route is ON while it diverges — i.e. the roads that
 * actually make this route different from the direct one. Maps each step to its
 * distance window (steps carry distance, not geometry) and keeps the ones whose
 * window overlaps a divergence span, longest-first.
 */
export function roadsDuringDivergence(
  steps: Array<{ roadName?: string; distance?: number }> | undefined,
  totalDistanceM: number,
  spans: DivergenceSpan[],
): string[] {
  if (!steps || steps.length === 0 || spans.length === 0 || totalDistanceM <= 0) return [];
  const inSpan = (f: number) => spans.some((s) => f >= s.startFrac && f <= s.endFrac);
  const meters = new Map<string, number>();
  let cum = 0;
  for (const st of steps) {
    const d = st.distance ?? 0;
    const startF = cum / totalDistanceM;
    const endF = (cum + d) / totalDistanceM;
    cum += d;
    const midF = (startF + endF) / 2;
    if (!st.roadName || !inSpan(midF)) continue;
    meters.set(st.roadName, (meters.get(st.roadName) ?? 0) + d);
  }
  return [...meters.entries()].sort((a, b) => b[1] - a[1]).map(([name]) => name);
}
